import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, LOCALE_ID, inject, provideAppInitializer } from '@angular/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import {
  Router,
  TitleStrategy,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TraceService } from '@sentry/angular';
import * as Sentry from '@sentry/angular';
import { de } from 'date-fns/locale';
import { provideHighcharts } from 'highcharts-angular';
import { provideMatomo, withRouter } from 'ngx-matomo-client';

import {
  DmbEnvironment,
  DmbTitleStrategy,
  I18n,
  appRoutes,
  httpErrorInterceptor,
  httpErrorSimulationInterceptor,
  wizardEffects,
  wizardFeature,
} from '@dmb/dmb-ui';

import { provideDmbEnvironment } from './environments/provide-dmb-environment';

const provideSentry = (env: DmbEnvironment) => {
  if (env.sentryDsn) {
    Sentry.init({
      dsn: env.sentryDsn,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 1.0,
      tracePropagationTargets: ['prod', /^https:\/\/kontor-new-media-gmbh.sentry\.io\/api/],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      normalizeDepth: 10,
    });
    return [
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
      { provide: TraceService, deps: [Router] },
      provideAppInitializer(() => {
        inject(TraceService);
      }),
    ];
  }
  return [];
};

const env = provideDmbEnvironment();

export const appConfig: ApplicationConfig = {
  providers: [
    // mat-select panels are as wide as their longest option, not as the select
    { provide: MAT_SELECT_CONFIG, useValue: { panelWidth: null } },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors(ngDevMode ? [httpErrorInterceptor, httpErrorSimulationInterceptor] : [httpErrorInterceptor]),
    ),
    provideAppInitializer(() => inject(I18n).init()),
    { provide: LOCALE_ID, useFactory: () => inject(I18n).locale },
    { provide: TitleStrategy, useClass: DmbTitleStrategy },
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
    ),
    { provide: DmbEnvironment, useValue: env },
    provideSentry(env),
    provideHighcharts(),
    provideStore(),
    provideState(wizardFeature),
    ...(ngDevMode ? [provideStoreDevtools()] : []),
    provideEffects(wizardEffects),
    { provide: MAT_DATE_LOCALE, useValue: de },
    provideDateFnsAdapter(),
    provideMatomo(
      {
        siteId: env.matomoConfig.siteId || 2,
        trackerUrl: env.matomoConfig.trackerUrl || '',
      },
      withRouter(),
    ),
  ],
};
